import { secondDriverOption } from '../configs/constants';
import { StepperFooter, toast, useAssertedParams, useStepperContext } from '@folio/index';
import { PolicyClaimsModule } from '@folio/modules';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { getVtcClaimsRefusals } from '@folio/utils/claims';
import { quoteFailedUserHelp } from '@folio/utils/quoteFailedHelp';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  claimsControllerListQueryOptions,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
} from 'korint-sdk';

type ClaimsStepProps = {
  mode?: 'new-business' | 'add-only';
};

export function ClaimsStep({ mode = 'new-business' }: ClaimsStepProps = {}) {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();

  const claimsConfig = usePolicyProductConfig('claims');

  const { isLoading: isClaimsListLoading, refetch: refetchClaims } = useQuery({
    ...claimsControllerListQueryOptions({
      externalId: policyId,
      pageInfo: { count: 100 },
    }),
    enabled: !!policyId,
    select: data => data.data,
  });

  const { data: policy } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    enabled: !!policyId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const hasSecondDriver = policy?.customFields?.[secondDriverOption.name];

  const claimsFromDate = dayjs().subtract(3, 'year').toDate();

  return (
    <div className="grid gap-4">
      <PolicyClaimsModule
        title={t('claims.driver1PastThreeYearsTitle')}
        claimsMetadata={{ driver: '1' }}
        fromDate={claimsFromDate}
      />

      {hasSecondDriver && (
        <PolicyClaimsModule
          title={t('claims.driver2PastThreeYearsTitle')}
          claimsMetadata={{ driver: '2' }}
          fromDate={claimsFromDate}
          mode={mode}
        />
      )}

      <StepperFooter
        nextButtonEnabled
        onNext={async () => {
          if (mode === 'add-only') {
            stepper.next();
            return;
          }

          const { data: claims = [] } = await refetchClaims();

          for (const claim of claims) {
            const claimCategoryConfig = claimsConfig.claimsCategories.find(
              ({ category }) => category === claim.category,
            );

            if (!claim.responsibility && claimCategoryConfig?.hasResponsibility) {
              toast({
                title: t('claims.incompleteClaimsToast.title'),
                description: t('claims.incompleteClaimsToast.description'),
              });

              return;
            }
          }

          const refusals = getVtcClaimsRefusals({
            claims,
            drivers: hasSecondDriver ? ['1', '2'] : ['1'],
            policyStartedAt: policy?.startedAt,
          });
          if (refusals.length > 0) {
            toast({ title: quoteFailedUserHelp(refusals) });
            return;
          }

          stepper.next();
        }}
        loading={isClaimsListLoading}
      />
    </div>
  );
}
